<script setup>
import { ref, inject, onMounted, onUnmounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useStorage } from '@/composables/storage'
import { useEchoStore } from '@/stores/echo'
import ringFile from '@/assets/ring.mp3'
import ringChatFile from '@/assets/chat.mp3'
import ringMessage from '@/assets/help.mp3'

// --- Начало импорта ваших SVG-иконок ---
// Импорты и маппинг основаны на icon-значениях из src/config/tree.js и вашем списке SVG-файлов.

// Для Header.vue (menu, shopping-cart, bell, user, power)
import AccessibleIcon from '@/assets/accessible.svg?component'; // Заглушка для 'menu', 'bell', 'power'
import ShoppingCartIcon from '@/assets/shopping-cart.svg?component';
import UserIcon from '@/assets/user.svg?component';

// --- Конец импорта ваших SVG-иконок ---

const storage = useStorage()
const router = useRouter()
const user = inject('$user')

if (!user) {
	router.push({ name: 'login' })
}

function setAction(key) {
	if (key === 'logout') {
		storage.clear()
		window.location.href = '/login'
	}
}

const echo = useEchoStore()

const menuState = ref('collapsed') // Это, возможно, избыточно здесь, если состояние меню управляется в MainMenu.vue
const menuToggleState = ref('collapsed') // Тоже, возможно, избыточно

function menuOpen() {
	document.body.classList.add('menu-expanded')
	document.body.classList.remove('menu-collapsed')
	menuToggleState.value = 'expanded'
	menuState.value = 'expanded'
}

function menuClose() { // Этот метод в Header.vue не используется напрямую в template, но может быть вызван откуда-то еще
	document.body.classList.add('menu-collapsed')
	document.body.classList.remove('menu-expanded')
	menuToggleState.value = 'collapsed'
	menuState.value = 'collapsed'
}

// мобильное меню пользователя
const sidebarOpen = ref(false)

function closeSidebarOnClickOutside(e) {
	const sidebar = document.querySelector('.mobile-user-sidebar')
	const toggle = document.querySelector('.mobile-user-toggle')

	if (
		sidebar &&
		!sidebar.contains(e.target) &&
		(!toggle || !toggle.contains(e.target))
	) {
		sidebarOpen.value = false
	}
}

onMounted(() => {
	document.addEventListener('click', closeSidebarOnClickOutside)
})
onUnmounted(() => {
	document.removeEventListener('click', closeSidebarOnClickOutside)
})

// --- Маппинг имен иконок к импортированным компонентам ---
// Ключи должны соответствовать значениям icon из src/config/tree.js
const iconComponents = {
  menu: AccessibleIcon, // Заглушка для 'menu'
  'shopping-cart': ShoppingCartIcon,
  bell: AccessibleIcon, // Заглушка для 'bell'
  user: UserIcon,
  power: AccessibleIcon, // Заглушка для 'power'
};

// Функция для получения компонента иконки по имени
function getIconComponent(iconName) {
  return iconComponents[iconName];
}
// --- Конец маппинга и функции ---
</script>

<template>
	<nav class="header-navbar navbar navbar-expand-lg align-items-center floating-nav navbar-light navbar-shadow">
		<div class="navbar-container d-flex content w-100 justify-content-between">
			
			<ul class="nav navbar-nav d-xl-none">
				<li class="nav-item">
					<a class="nav-link menu-toggle" href="#" @click.prevent="menuOpen">
						<vue-feather type="menu" />
					</a>
				</li>
			</ul>

			<audio id="sound-file">
				<source :src="ringFile" type="audio/mpeg" />
			</audio>

      <audio id="sound-fileChat">
        <source :src="ringChatFile" type="audio/mpeg" />
      </audio>

      <audio id="sound-fileMesaage">
        <source :src="ringMessage" type="audio/mpeg" />
      </audio>



      <ul class="nav navbar-nav align-items-center ms-auto">
				<li class="nav-item dropdown dropdown-cart me-25">
					<RouterLink :to="{ name: 'orders', query: { status: 'new' } }" class="nav-link">
						<vue-feather type="shopping-cart" size="21" />
						<span class="badge rounded-pill bg-primary badge-up">{{ echo.newOrders }}</span>
					</RouterLink>
				</li>

        <li class="nav-item dropdown dropdown-notification me-25">
          <RouterLink :to="{ name: 'tickets' }" class="nav-link">
            <vue-feather type="help-circle" size="21" />
            <span v-if="echo.newTicketMessages > 0" class="badge rounded-pill bg-danger badge-up">
            {{ echo.newTicketMessages }}
          </span>
          </RouterLink>
        </li>

        <li class="nav-item dropdown dropdown-notification me-25">
          <RouterLink :to="{ name: 'chats' }" class="nav-link">
            <vue-feather type="message-square" size="21" />
            <span v-if="echo.newChatMessages > 0" class="badge rounded-pill bg-info badge-up">
            {{ echo.newChatMessages }}
          </span>
          </RouterLink>
        </li>

				
				<li v-if="user" class="nav-item">
					<a href="#" class="nav-link mobile-user-toggle d-xl-none" @click.prevent="sidebarOpen = true">
						<vue-feather type="user" />
					</a>

					
					<div class="dropdown dropdown-user d-none d-xl-block">
						<a
							class="nav-link dropdown-toggle dropdown-user-link"
							href="#"
							data-bs-toggle="dropdown"
							aria-haspopup="true"
							aria-expanded="false">
							<div class="user-nav d-sm-flex d-none">
								<span class="user-name fw-bolder">{{ user.email }}</span>
							</div>
						</a>
						<div class="dropdown-menu dropdown-menu-end">
							<template v-for="(item, key) in $config.tree.account" :key="key">
								<button
									v-if="item?.action"
									@click="setAction(item.action)"
									class="dropdown-item d-block w-100">
									<vue-feather class="me-50" :type="item.icon" />
									{{ $lang('menu.' + key) }}
								</button>
								<RouterLink v-else :to="{ name: key }" class="dropdown-item">
									<vue-feather class="me-50" :type="item.icon" />
									{{ $lang('menu.' + key) }}
								</RouterLink>
							</template>
						</div>
					</div>
				</li>
			</ul>
		</div>
	</nav>

	
	<transition name="fade">
		<div v-if="sidebarOpen" class="user-overlay"></div>
	</transition>

	
	<transition name="slide">
		<div v-if="sidebarOpen" class="mobile-user-sidebar">
			<div class="mobile-user-header p-2 border-bottom d-flex justify-content-between align-items-center">
				<strong>{{ user.email }}</strong>
				<button class="btn-close" @click="sidebarOpen = false">×</button>
			</div>
			<div class="mobile-user-body p-2">
				<template v-for="(item, key) in $config.tree.account" :key="key">
					<button
						v-if="item?.action"
						@click="setAction(item.action)"
						class="btn w-100 text-start mb-2">
						<vue-feather class="me-1" :type="item.icon" />
						{{ $lang('menu.' + key) }}
					</button>
					<RouterLink
						v-else
						:to="{ name: key }"
						class="btn w-100 text-start mb-2"
						@click="sidebarOpen = false">
						<vue-feather class="me-1" :type="item.icon" />
						{{ $lang('menu.' + key) }}
					</RouterLink>
				</template>
			</div>
		</div>
	</transition>
</template>

<style>
/* Затемнение */
.user-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.4);
	z-index: 1040;
}

/* Мобильная панель */
.mobile-user-sidebar {
	position: fixed;
	top: 0;
	right: 0;
	width: 260px;
	height: 100vh;
	background: white;
	z-index: 1050;
	box-shadow: -2px 0 12px rgba(0, 0, 0, 0.2);
	display: flex;
	flex-direction: column;
}

/* Анимации */
.slide-enter-from,
.slide-leave-to {
	transform: translateX(100%);
	opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
	transition: transform 0.3s ease, opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

/* Кнопка закрытия */
.btn-close {
	background: none;
	border: none;
	font-size: 1.5rem;
	line-height: 1;
	cursor: pointer;
	color: #000;
}
</style>
